'use client'

import React from 'react'
import { usePathname } from 'next/navigation'
import { useTranslation } from 'react-i18next'
import type { DrawerProps, MenuProps } from 'antd'
import { Drawer, Menu } from 'antd'
import cn from 'classnames'
import type { ISideMenu } from '../..'
import IconFont from '@/app/components/icon-font'
import { getSelectedKey } from '@/app/components/mlops/Sidebar'
import './index.css'

export type ICollapsibleSidebarProps = {
  title?: string
  menus?: ISideMenu[]
  collapsed?: boolean
  onCollapsed?: (_collapsed: boolean) => void
  onItemClick?: (_menu: ISideMenu, _domEvent: any) => void
  onVisibleChanged?: (_open: boolean) => void
} & DrawerProps

export function getIcon(menu: ISideMenu) {
  if (menu.icon) {
    return `icon-tkr${menu.icon}`
  }
  if (menu.path?.startsWith('/iaas') || menu.path?.startsWith('/llm')) {
    const res = menu.path.split('/').splice(2)
    return `icon-tkrres-${res.join('-')}`
  }
  return 'icon-tkrmenu-appstore'
}

function getMenuIcon(menu: ISideMenu) {
  return (
    <div className='sidebar-menu-icon'>
      <IconFont type={getIcon(menu)} width={18} />
    </div>
  )
}

function processMenu(
  menus: ISideMenu[],
  collapsed = false,
  onItemClick?: (_m: ISideMenu, _domEvent: any) => void,
  t?: (_key: string) => string,
): MenuProps['items'] {
  const walk = (menu: ISideMenu) => {
    const children: any = menu.children?.map(m => walk(m))
    const icon = getMenuIcon(menu)
    // console.log(t(`routerConfig.${menu.title}`))
    const label =
      menu.msg && t
        ? t(menu.msg)
        : t?.(`routerConfig.${menu.title}`)
    const res: NonNullable<MenuProps['items']>[number] = {
      label: collapsed ? '' : (label as string),
      title: label as string,
      icon,
      key: menu.path || menu.title,
      children,
      type: menu.type,
      onClick: ({ domEvent }) => onItemClick?.(menu, domEvent),
    }
    return res
  }
  return menus.map(m => walk(m))
}

const CollapsibleSidebar = (props: ICollapsibleSidebarProps) => {
  const {
    title,
    menus,
    collapsed,
    open,
    onCollapsed,
    onItemClick,
    ...drawerProps
  } = props
  const { t } = useTranslation()
  const items = processMenu(menus ?? [], collapsed, onItemClick, t)
  const pathname = usePathname()
  const basePath = process.env.NEXT_PUBLIC_BASE_URL

  const wrapperClass = cn('collapsible-sidebar-wrap', {
    collapsed,
    closed: !open,
  })

  const titleWrapClass = cn(
    'flex',
    'title-wrap',
    'pt-3',
    'pb-3',
    'pr-3',
    'justify-between',
    {
      'pl-3': collapsed,
      collapsed,
    },
  )

  return (
    <div className={wrapperClass}>
      <Drawer
        {...drawerProps}
        open={open}
        placement='left'
        mask={false}
        styles={{
          body: { padding: '0px' },
          wrapper: { width: collapsed ? '50px' : '160px' },
        }}
        closable={false}>
        <div className={titleWrapClass}>
          {!collapsed && (
            <span className='title me-auto'>
              {title &&
                t(
                  title.startsWith('nav.')
                    ? title
                    : `routerConfig.${title}`,
                )}
            </span>
          )}
          <div
            onClick={() => onCollapsed?.(!collapsed)}
            className={cn({ 'mx-auto': collapsed })}>
            {collapsed ? (
              <IconFont
                size={20}
                type='icon-tkrcircle-right'
                className='title-arrow'
              />
            ) : (
              <IconFont
                size={20}
                type='icon-tkrcircle-left'
                className='title-arrow'
              />
            )}
          </div>
        </div>
        <Menu
          items={items}
          selectedKeys={[
            getSelectedKey(basePath + pathname, menus || []) ||
              '/llm/ecs/overview',
          ]}
          selectable={true}
          className='submenu'
          {...(collapsed ? { inlineCollapsed: collapsed } : {})}
          mode={collapsed ? 'inline' : 'vertical'}
        />
      </Drawer>
    </div>
  )
}

export default React.memo(CollapsibleSidebar)
